import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Mail, MessageSquare, Twitter, Loader2, Send, MapPin } from "lucide-react";

export const CONTACT_EMAIL = "support@grantflow.app";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", org_name: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const change = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/api/contact/", form);
      toast.success("Thanks — we'll reply within one business day.");
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-14 grid lg:grid-cols-5 gap-10" data-testid="contact-page">
        <div className="lg:col-span-2">
          <div className="overline mb-3">GET IN TOUCH</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight" data-testid="contact-title">Real humans. Real replies.</h1>
          <p className="mt-4 text-neutral-600 leading-relaxed">
            Questions about eligibility, refunds, billing, or a specific grant? Message us and a human will respond within
            one business day.
          </p>
          <ul className="mt-8 space-y-4">
            <li className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-neutral-100 flex items-center justify-center"><Mail size={16} /></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500">Support</div>
                <a href={`mailto:${CONTACT_EMAIL}`} className="font-medium hover:underline" data-testid="contact-email-link">{CONTACT_EMAIL}</a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-neutral-100 flex items-center justify-center"><MessageSquare size={16} /></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500">Sales &amp; partnerships</div>
                <a href={`mailto:hello@grantflow.app`} className="font-medium hover:underline">hello@grantflow.app</a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-neutral-100 flex items-center justify-center"><MapPin size={16} /></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500">Mailing</div>
                <div className="font-medium">GrantFlow, Inc.<br />1 Market St #4200, San Francisco, CA 94105</div>
              </div>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-3">
          {sent ? (
            <div className="gf-card p-10 text-center" data-testid="contact-success">
              <div className="h-14 w-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto"><Send size={22} /></div>
              <h2 className="font-display text-3xl font-bold mt-6">Message sent</h2>
              <p className="text-neutral-600 mt-2">We'll reply to <span className="font-medium">{form.email}</span> within one business day.</p>
              <button onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "", message: "", org_name: "" }); }} className="btn-ghost mt-6">Send another</button>
            </div>
          ) : (
            <form onSubmit={submit} className="gf-card p-6 md:p-8 space-y-4" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Your name</label>
                  <input required value={form.name} onChange={(e) => change("name", e.target.value)}
                    className="mt-1 w-full h-11 border border-neutral-200 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-neutral-900"
                    data-testid="contact-name" />
                </div>
                <div>
                  <label className="text-sm font-medium">Email</label>
                  <input required type="email" value={form.email} onChange={(e) => change("email", e.target.value)}
                    className="mt-1 w-full h-11 border border-neutral-200 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-neutral-900"
                    data-testid="contact-email" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Organization (optional)</label>
                <input value={form.org_name} onChange={(e) => change("org_name", e.target.value)}
                  className="mt-1 w-full h-11 border border-neutral-200 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-neutral-900"
                  data-testid="contact-org" />
              </div>
              <div>
                <label className="text-sm font-medium">Subject</label>
                <input required value={form.subject} onChange={(e) => change("subject", e.target.value)}
                  placeholder="Billing, eligibility, refund, feature request..."
                  className="mt-1 w-full h-11 border border-neutral-200 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-neutral-900"
                  data-testid="contact-subject" />
              </div>
              <div>
                <label className="text-sm font-medium">Message</label>
                <textarea required rows={6} value={form.message} onChange={(e) => change("message", e.target.value)}
                  placeholder="Tell us what's on your mind. The more detail, the faster we can help."
                  className="mt-1 w-full border border-neutral-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-neutral-900"
                  data-testid="contact-message" />
              </div>
              <button disabled={loading} className="btn-primary" data-testid="contact-submit">
                {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <><Send size={16} /> Send message</>}
              </button>
              <p className="text-xs text-neutral-500">We reply within one business day. For urgent billing issues include "URGENT" in the subject.</p>
            </form>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
